import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Calendar, Card, Col, ConfigProvider, Descriptions, Empty, Input, Modal, Result, Row, Select, Spin, Tag, message } from 'antd'
import thTH from 'antd/lib/locale/th_TH'
import { SearchOutlined, UserOutlined } from '@ant-design/icons'
import { get } from 'lodash'
import moment from 'moment'
import 'moment/locale/th'
import Layout from '../Home'
import { ROLES } from '../../constant'
import { formatHn, toRawHn } from '../../utils/hn'
import FCM from '../../models/FCM'
import {
  createBooking,
  findPatientByCidOrHn,
  getDoctorAvailableTime,
  getDoctorBookings,
  getDoctorTypes,
  getDoctorsByOtherType,
  getDoctorsByType,
  getFcmTokensByOmaId,
  getHieHnByCid,
  getOtherTypes,
  getTelemedPatientId,
  getThaiHolidays,
  updatePatientHn,
} from '../../apiServices/bookingServices'
import {
  firstSelectableDate,
  isDateSelectable,
  minuteOfWeekToLabel,
  slotsForDate,
  takenSlotsForDate,
  toNotificationTime,
} from './utils'

const GREEN = '#0A905F'
const GREEN_SOFT = '#EAF7F1'

// คอลเซ็นเตอร์เป็นคนจัดคิวเองอยู่แล้ว นัดที่สร้างจากหน้านี้จึงข้าม PATIENT_DRAFT
// (= "รอคอลเซ็นเตอร์ดำเนินการ") ไปรอแพทย์ได้เลย เหมือนที่ patchBooking ทำหลังจัดคิวเสร็จ
const BOOKING_STATUS_ON_CREATE = 'DOCTOR_PENDING'
// bookingDevice เป็นช่องเดียวที่บอกที่มาของนัด (มือถือเก็บยี่ห้อ+รุ่นเครื่อง)
// จึงใช้บันทึกว่าเจ้าหน้าที่คนไหนเป็นคนนัด เพื่อให้สืบย้อนหลังได้
// ใช้อีเมลเป็นหลักเพราะไม่ซ้ำและเป็นตัวตนที่พอร์ทัลใช้แสดงเจ้าหน้าที่อยู่แล้ว
// (DoctorSetting/patientbooking.js) ส่วน fullname ของบางบัญชีตั้งเป็นชื่อกลาง ๆ ระบุตัวคนไม่ได้
const bookingDeviceFor = (staff) => {
  const who = (staff && (staff.email || staff.fullname)) || ''
  return who ? `Call Center · ${who}` : 'Call Center (web portal)'
}

// แถบข้อมูลผู้ป่วยแบบ key details bar ของงานฝั่งเจ้าหน้าที่ เจ้าหน้าที่คุยโทรศัพท์
// ไปเลือกคิวไป จึงต้องเห็นว่ากำลังนัดให้ใครอยู่ตลอดโดยไม่ต้องเลื่อนกลับขึ้นไปดู
const PatientBanner = ({ patient, roleLabel, hnNode, onChangePatient }) => (
  <div
    style={{
      position: 'sticky',
      top: 0,
      zIndex: 5,
      background: GREEN_SOFT,
      borderLeft: `4px solid ${GREEN}`,
      borderRadius: 4,
      padding: '12px 16px',
      marginBottom: 16,
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 24,
    }}
  >
    <div style={{ fontSize: 18, fontWeight: 'bold', color: '#095C3E' }}>
      <UserOutlined style={{ marginRight: 8 }} />
      {patient.name || 'ไม่ระบุชื่อ'}
    </div>
    <BannerField label="HN" value={hnNode} />
    <BannerField label="เลขบัตรประชาชน" value={patient.cId || '-'} />
    <BannerField
      label="ยืนยันตัวตน"
      value={patient.verifyId ? <Tag color="green">ยืนยันแล้ว</Tag> : <Tag color="red">ยังไม่ยืนยัน</Tag>}
    />
    <BannerField label="กำลังนัดกับ" value={roleLabel} />
    <div style={{ marginLeft: 'auto' }}>
      <Button onClick={onChangePatient}>เปลี่ยนผู้ป่วย</Button>
    </div>
  </div>
)

const BannerField = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 12, color: '#6b6b6b' }}>{label}</div>
    <div style={{ fontWeight: 500 }}>{value}</div>
  </div>
)

const Content = ({ userInfo }) => {
  const [searchInput, setSearchInput] = useState('')
  const [searching, setSearching] = useState(false)
  const [patient, setPatient] = useState(null)
  const [patientError, setPatientError] = useState(null)

  const [doctorTypes, setDoctorTypes] = useState([])
  const [otherTypes, setOtherTypes] = useState([])
  // ค่าเป็น "doctor:<id>" หรือ "other:<id>" เพราะสองแหล่งมี id ชนกันได้
  const [department, setDepartment] = useState(null)
  const [doctors, setDoctors] = useState([])
  const [doctorsLoading, setDoctorsLoading] = useState(false)
  const [doctorId, setDoctorId] = useState(null)

  const [availableTime, setAvailableTime] = useState(null)
  const [doctorBookings, setDoctorBookings] = useState([])
  const [scheduleLoading, setScheduleLoading] = useState(false)
  const [scheduleError, setScheduleError] = useState(null)

  const [holidays, setHolidays] = useState([])
  const [date, setDate] = useState(null)
  // เดือนที่ปฏิทินเปิดอยู่ แยกจากวันที่เลือก เพื่อให้เลื่อนดูเดือนอื่นได้โดยไม่เสียคิวที่เลือกไว้
  const [calendarMonth, setCalendarMonth] = useState(moment())
  const [slot, setSlot] = useState(null)

  // HN ที่ดึงจาก HIE มาได้แต่ยังไม่ได้บันทึกลง OMA
  const [hieHn, setHieHn] = useState(null)
  const [hieHnLoading, setHieHnLoading] = useState(false)
  const [savingHn, setSavingHn] = useState(false)

  const [confirmVisible, setConfirmVisible] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    Promise.all([getDoctorTypes(), getOtherTypes()])
      .then(([types, others]) => {
        setDoctorTypes(Array.isArray(types) ? types : [])
        setOtherTypes(Array.isArray(others) ? others : [])
      })
      .catch(() => message.error('ไม่สามารถโหลดรายการแผนกได้'))
    // วันหยุดเป็นข้อมูลเสริม ถ้าดึงไม่ได้ก็ยังนัดได้ตามปกติ
    getThaiHolidays()
      .then(setHolidays)
      .catch(() => setHolidays([]))
  }, [])

  const doctor = useMemo(() => doctors.find((item) => item.id === doctorId) || null, [doctors, doctorId])

  // ป้ายชื่อวิชาชีพแบบเดียวกับ bookingTable (ROLES[doctor.roles[0].name]) เพราะแผนก
  // วิชาชีพอื่นไม่ได้มีแต่แพทย์ ข้อความแจ้งเตือนจะได้ตรงกับที่ผู้ป่วยเห็นในแอป
  const providerRoleLabel = useMemo(() => {
    const roleName = get(doctor, 'roles[0].name')
    return (roleName && ROLES[roleName]) || 'แพทย์'
  }, [doctor])

  const daySlots = useMemo(() => slotsForDate(availableTime, date), [availableTime, date])
  const takenSlots = useMemo(() => takenSlotsForDate(doctorBookings, date), [doctorBookings, date])

  const resetSchedule = () => {
    setAvailableTime(null)
    setDoctorBookings([])
    setScheduleError(null)
    setDate(null)
    setSlot(null)
    setCalendarMonth(moment())
  }

  const onSearchPatient = async () => {
    const keyword = String(searchInput || '').trim()
    if (!keyword) {
      message.warning('กรุณากรอกเลขบัตรประชาชนหรือ HN ของผู้ป่วย')
      return
    }
    setSearching(true)
    setPatient(null)
    setPatientError(null)
    try {
      // เจ้าหน้าที่พิมพ์ HN แบบ 28318/66 แต่ฐานข้อมูลเก็บเป็น 660028318
      const matches = await findPatientByCidOrHn(toRawHn(keyword))
      const info = matches.find((item) => item && item.userId)
      if (!info) {
        setPatientError('ไม่พบผู้ป่วยที่ใช้เลขบัตรประชาชนหรือ HN นี้')
        return
      }
      if (matches.length > 1) {
        message.warning(`พบผู้ป่วยที่ตรงกัน ${matches.length} ราย แสดงรายการแรก กรุณาตรวจสอบชื่อให้ตรงก่อนนัดหมาย`)
      }
      const patientId = await getTelemedPatientId(info.userId)
      if (!patientId) {
        setPatientError('ผู้ป่วยรายนี้ยังไม่เคยเข้าใช้งานในแอปพลิเคชัน จึงยังนัดหมายให้ไม่ได้ กรุณาให้ผู้ป่วยเข้าแอปหนึ่งครั้งก่อน')
        return
      }
      setPatient({
        userInfoId: info.id,
        everOmaId: info.userId,
        patientId,
        cId: info.cId || '',
        hnId: info.hnId || '',
        name: [info.firstname, info.lastname].filter(Boolean).join(' '),
        verifyId: !!info.verifyId,
      })
      // ผู้ป่วยที่ HIE ยังไม่ถูก trigger จะไม่มี HN ใน OMA ลองดึงจาก HIE มาเสนอให้บันทึก
      if (!info.hnId && info.cId) {
        lookupHieHn(info.cId)
      }
    } catch (error) {
      const status = get(error, 'response.status')
      if (status === 401 || status === 403) {
        setPatientError('บัญชีของคุณไม่มีสิทธิ์ค้นหาข้อมูลผู้ป่วย')
      } else {
        setPatientError('เกิดข้อผิดพลาดในการค้นหาผู้ป่วย กรุณาลองใหม่อีกครั้ง')
      }
    } finally {
      setSearching(false)
    }
  }

  const lookupHieHn = async (cId) => {
    setHieHnLoading(true)
    try {
      setHieHn(await getHieHnByCid(cId))
    } catch (error) {
      setHieHn(null)
    } finally {
      setHieHnLoading(false)
    }
  }

  // ให้เจ้าหน้าที่กดยืนยันเอง ไม่เขียนทับข้อมูลผู้ป่วยเงียบ ๆ จากหน้าค้นหา
  const onSaveHn = async () => {
    if (!patient || !hieHn) {
      return
    }
    setSavingHn(true)
    try {
      await updatePatientHn(patient.userInfoId, hieHn)
      setPatient({ ...patient, hnId: hieHn })
      setHieHn(null)
      message.success('บันทึก HN ให้ผู้ป่วยเรียบร้อยแล้ว')
    } catch (error) {
      const status = get(error, 'response.status')
      message.error(
        status === 401 || status === 403
          ? 'บัญชีของคุณไม่มีสิทธิ์แก้ไขข้อมูลผู้ป่วย'
          : 'บันทึก HN ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง'
      )
    } finally {
      setSavingHn(false)
    }
  }

  const onChangePatient = () => {
    setPatient(null)
    setPatientError(null)
    setHieHn(null)
    setSearchInput('')
    setDepartment(null)
    setDoctorId(null)
    setDoctors([])
    resetSchedule()
  }

  const onSelectDepartment = async (value) => {
    setDepartment(value)
    setDoctorId(null)
    setDoctors([])
    resetSchedule()
    setDoctorsLoading(true)
    const [kind, id] = value.split(':')
    try {
      const response = kind === 'other' ? await getDoctorsByOtherType(id) : await getDoctorsByType(id)
      // แอปผู้ป่วยแสดงเฉพาะคนที่ยืนยันแล้ว (app/screens/TeleDoctor/index.js)
      // ถ้าไม่กรองเหมือนกัน คอลเซ็นเตอร์จะเห็นชื่อที่ผู้ป่วยเลือกเองไม่ได้
      const verified = (Array.isArray(response) ? response : []).filter(
        (item) => item.verified && item.emailVerified
      )
      setDoctors(verified)
      if (!verified.length) {
        message.info('ไม่มีผู้ให้บริการที่ยืนยันตัวตนแล้วในแผนกนี้')
      }
    } catch (error) {
      message.error('ไม่สามารถโหลดรายชื่อผู้ให้บริการในแผนกนี้ได้')
    } finally {
      setDoctorsLoading(false)
    }
  }

  const onSelectDoctor = async (value) => {
    setDoctorId(value)
    resetSchedule()
    setScheduleLoading(true)
    try {
      const [times, bookings] = await Promise.all([getDoctorAvailableTime(value), getDoctorBookings(value)])
      if (!times || !Array.isArray(times.time) || !times.time.length) {
        setScheduleError('ผู้ให้บริการท่านนี้ยังไม่ได้ตั้งตารางเวลาออกตรวจ กรุณาเลือกท่านอื่น')
        return
      }
      setAvailableTime(times)
      setDoctorBookings(Array.isArray(bookings) ? bookings : [])
      const firstDay = firstSelectableDate(times, holidays)
      if (firstDay) {
        setCalendarMonth(firstDay)
      } else {
        setScheduleError('ไม่พบวันที่ว่างของผู้ให้บริการท่านนี้ใน 60 วันข้างหน้า')
      }
    } catch (error) {
      setScheduleError('ไม่สามารถโหลดตารางเวลาของผู้ให้บริการได้ กรุณาลองใหม่อีกครั้ง')
    } finally {
      setScheduleLoading(false)
    }
  }

  const disabledDate = useCallback(
    (current) => !isDateSelectable(availableTime, holidays, current),
    [availableTime, holidays]
  )

  const onSelectDate = (value) => {
    if (disabledDate(value)) {
      return
    }
    setDate(value)
    setCalendarMonth(value)
    setSlot(null)
  }

  const canSubmit = !!(patient && patient.verifyId && doctor && date && slot !== null)

  const onConfirm = async () => {
    if (!canSubmit) {
      return
    }
    setSubmitting(true)
    const admitTime = date.format('YYYY-MM-DD')
    try {
      // กันคิวชนกับคนที่จองแทรกระหว่างที่หน้าจอเปิดค้างไว้
      const latest = await getDoctorBookings(doctor.id)
      const latestTaken = takenSlotsForDate(latest, date)
      if (latestTaken.includes(slot)) {
        setDoctorBookings(Array.isArray(latest) ? latest : [])
        setSlot(null)
        setConfirmVisible(false)
        message.error('คิวนี้เพิ่งถูกจองไปแล้ว กรุณาเลือกเวลาอื่น')
        return
      }

      const departmentName = get(doctor, 'detail.department', '')
      const booking = await createBooking({
        admitTime,
        bookingTime: slot,
        // AppUserIdentity.appUserId เป็น string แต่นัดที่มือถือสร้างเก็บ patientId เป็นตัวเลข
        // ถ้าบันทึกเป็น string ไว้ Bookings/filterByPatientId ที่แอปผู้ป่วยใช้จะ match ไม่เจอ
        // แล้วนัดจะไม่โผล่ในแอป
        patientId: Number(patient.patientId),
        doctorId: Number(doctor.id),
        bookingEndTime: 0,
        bookingDevice: bookingDeviceFor(userInfo),
        bookingType: departmentName === 'ARI (OPSI)' ? departmentName : 'normal',
        status: BOOKING_STATUS_ON_CREATE,
      })
      if (!booking || !booking.id) {
        message.error('สร้างการนัดหมายไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
        return
      }

      await notifyPatient(admitTime)
      message.success('สร้างการนัดหมายให้ผู้ป่วยเรียบร้อยแล้ว')
      setConfirmVisible(false)
      setSlot(null)
      try {
        const refreshed = await getDoctorBookings(doctor.id)
        setDoctorBookings(Array.isArray(refreshed) ? refreshed : [])
      } catch (error) {
        // โหลดคิวใหม่ไม่สำเร็จ ไม่กระทบนัดที่สร้างไปแล้ว จึงไม่ต้องแจ้งว่าล้มเหลว
      }
    } catch (error) {
      message.error('สร้างการนัดหมายไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
    } finally {
      setSubmitting(false)
    }
  }

  // แจ้งเตือนเข้าแอปผู้ป่วยแบบเดียวกับ BookingFromPatient.fetchUserFcmToken
  // ล้มเหลวได้โดยไม่ทำให้การนัดหมายที่สร้างสำเร็จแล้วพัง
  const notifyPatient = async (admitTime) => {
    try {
      const tokens = await getFcmTokensByOmaId(patient.everOmaId)
      if (!Array.isArray(tokens) || !tokens.length) {
        return
      }
      const notification = {
        body: `เจ้าหน้าที่ได้นัดหมาย${providerRoleLabel}ให้คุณวันที่ ${moment(admitTime).format('D MMM YYYY')} เวลา ${minuteOfWeekToLabel(slot)} น.`,
        title: 'แจ้งเตือน',
        icon: 'ic_launcher',
        color: '#f1c40f',
        sound: 'default',
      }
      const data = {
        bookingTime: toNotificationTime(admitTime, slot),
        screen: 'MyBookingsUI',
      }
      await Promise.all(tokens.map((item) => FCM.sendMessage(item.fcmToken, notification, data)))
    } catch (error) {
      message.warning('สร้างการนัดหมายแล้ว แต่ส่งแจ้งเตือนเข้าแอปผู้ป่วยไม่สำเร็จ')
    }
  }

  // userInfo เริ่มต้นเป็น {} แล้วค่อยถูกเติมจาก redux ระหว่างนั้นยังตัดสินสิทธิ์ไม่ได้
  if (!userInfo || !Array.isArray(userInfo.roles)) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    )
  }
  const roles = userInfo.roles.map((item) => item.name)
  if (!roles.includes('callCenter')) {
    return <Result status="403" title="ไม่มีสิทธิ์เข้าถึง" subTitle="หน้านี้สำหรับเจ้าหน้าที่คอลเซ็นเตอร์เท่านั้น" />
  }

  // antd จะไฮไลต์เซลล์ที่ตรงกับ value ซึ่งเป็นเดือนที่เปิดอยู่ ไม่ใช่วันที่เลือกจริง
  // จึงวาดเซลล์เองให้ไฮไลต์ตามวันที่เลือกจริง และไม่สื่อสารด้วยสีอย่างเดียว
  // HN มาได้ 3 ทาง: บันทึกไว้แล้ว / ดึงสดจาก HIE ที่ยังไม่บันทึก / ไม่มีเลย
  const renderHnField = () => {
    if (patient.hnId) {
      return formatHn(patient.hnId)
    }
    if (hieHnLoading) {
      return <Spin size="small" />
    }
    if (hieHn) {
      return (
        <span>
          <span style={{ color: '#8a6d1f' }}>{formatHn(hieHn)}</span>
          <Tag color="orange" style={{ marginLeft: 8 }}>
            จาก HIE ยังไม่บันทึก
          </Tag>
          <Button type="link" size="small" style={{ padding: 0 }} loading={savingHn} onClick={onSaveHn}>
            บันทึก HN นี้
          </Button>
        </span>
      )
    }
    return <span style={{ color: '#999' }}>ไม่มีในระบบและ HIE</span>
  }

  const renderDateCell = (current) => {
    const isSelected = !!date && current.isSame(date, 'day')
    const isSelectable = !disabledDate(current)
    return (
      <div
        className="ant-picker-cell-inner ant-picker-calendar-date"
        style={{
          background: isSelected ? GREEN : 'transparent',
          // ต้องระบุสีตัวอักษรทุกกรณี ปล่อยเป็น undefined ไม่ได้ เพราะ antd ใส่คลาส
          // selected ให้เซลล์ที่ตรงกับ value (= เดือนที่เปิดอยู่) แล้วบังคับสีขาว
          // ทำให้วันนั้นกลายเป็นขาวบนขาวจนหายไปทั้งที่กดได้
          color: isSelected ? '#fff' : isSelectable ? 'rgba(0, 0, 0, 0.85)' : '#ccc',
          borderColor: isSelected ? GREEN : undefined,
          fontWeight: isSelected ? 'bold' : undefined,
        }}
      >
        <div className="ant-picker-calendar-date-value">{current.date()}</div>
      </div>
    )
  }

  const renderSlots = () => {
    if (!doctor) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="เลือกแผนกและผู้ให้บริการก่อน" />
    }
    if (scheduleError) {
      return <Alert type="warning" showIcon message={scheduleError} />
    }
    if (!date) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="เลือกวันที่จากปฏิทินทางซ้าย" />
    }
    if (!daySlots.length) {
      return <Alert type="info" showIcon message="ผู้ให้บริการไม่มีตารางว่างในวันดังกล่าว" />
    }
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {daySlots.map((period) => {
          const taken = takenSlots.includes(period)
          return (
            <Button
              key={period}
              type={slot === period ? 'primary' : 'default'}
              disabled={taken}
              onClick={() => setSlot(period)}
              style={{ minWidth: 88 }}
            >
              {/* ไม่สื่อสารด้วยสีอย่างเดียว คิวเต็มมีคำว่า "เต็ม" กำกับด้วย */}
              {minuteOfWeekToLabel(period)}
              {taken ? ' · เต็ม' : ''}
            </Button>
          )
        })}
      </div>
    )
  }

  return (
    <div style={{ padding: 24, width: '100%' }}>
      <h1 style={{ color: GREEN, marginBottom: 2 }}>นัดหมายให้ผู้ป่วย</h1>
      <div style={{ color: '#888', marginBottom: 16 }}>
        สำหรับผู้ป่วยที่ลงทะเบียนและเคยเข้าใช้งานในแอปพลิเคชันแล้วเท่านั้น
      </div>

      {!patient ? (
        <Card title="ค้นหาผู้ป่วย" style={{ maxWidth: 640 }}>
          <Input.Search
            placeholder="เลขบัตรประชาชน หรือ HN (เช่น 28318/66)"
            enterButton={
              <span>
                <SearchOutlined /> ค้นหา
              </span>
            }
            size="large"
            value={searchInput}
            loading={searching}
            onChange={(e) => setSearchInput(e.target.value)}
            onSearch={onSearchPatient}
          />
          {patientError ? <Alert style={{ marginTop: 12 }} type="error" showIcon message={patientError} /> : null}
        </Card>
      ) : (
        <>
          <PatientBanner
            patient={patient}
            roleLabel={providerRoleLabel}
            hnNode={renderHnField()}
            onChangePatient={onChangePatient}
          />

          {!patient.verifyId ? (
            <Alert
              style={{ marginBottom: 16 }}
              type="warning"
              showIcon
              message="ผู้ป่วยยังไม่ได้ยืนยันตัวตน"
              description="แอปผู้ป่วยไม่อนุญาตให้นัดหมายจนกว่าจะยืนยันตัวตน กรุณาให้เจ้าหน้าที่ยืนยันตัวตนก่อน"
            />
          ) : null}

          <Row gutter={16}>
            <Col xs={24} lg={9}>
              <Card title="แผนกและผู้ให้บริการ" style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 4 }}>แผนก</div>
                <Select
                  style={{ width: '100%', marginBottom: 12 }}
                  placeholder="เลือกแผนก"
                  value={department || undefined}
                  onChange={onSelectDepartment}
                  disabled={!patient.verifyId}
                  showSearch
                  optionFilterProp="label"
                >
                  <Select.OptGroup label="แพทย์">
                    {doctorTypes.map((type) => (
                      <Select.Option key={`doctor:${type.id}`} value={`doctor:${type.id}`} label={type.name}>
                        {type.name}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                  <Select.OptGroup label="วิชาชีพอื่น">
                    {otherTypes.map((type) => (
                      <Select.Option key={`other:${type.id}`} value={`other:${type.id}`} label={type.name}>
                        {type.name}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                </Select>

                <div style={{ marginBottom: 4 }}>ผู้ให้บริการ</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="เลือกผู้ให้บริการ"
                  value={doctorId || undefined}
                  loading={doctorsLoading}
                  onChange={onSelectDoctor}
                  disabled={!department || doctorsLoading}
                  notFoundContent="ไม่พบผู้ให้บริการในแผนกนี้"
                  showSearch
                  optionFilterProp="label"
                >
                  {doctors.map((item) => {
                    const name = item.fullname || item.email
                    const subtitle = get(item, 'detail.specialist') || get(item, 'detail.hospital') || ''
                    return (
                      <Select.Option key={item.id} value={item.id} label={`${name} ${subtitle}`}>
                        {name}
                        {subtitle ? <span style={{ color: '#999' }}> — {subtitle}</span> : null}
                      </Select.Option>
                    )
                  })}
                </Select>
              </Card>

              <Card title="เลือกวันที่" bodyStyle={{ padding: 8 }}>
                <Spin spinning={scheduleLoading}>
                  <ConfigProvider locale={thTH}>
                  <Calendar
                    fullscreen={false}
                    value={calendarMonth}
                    disabledDate={disabledDate}
                    onSelect={onSelectDate}
                    onPanelChange={(value) => setCalendarMonth(value)}
                    dateFullCellRender={renderDateCell}
                  />
                  </ConfigProvider>
                  <div style={{ color: '#888', padding: '4px 8px 8px' }}>
                    นัดล่วงหน้าอย่างน้อย 1 วัน ไม่รวมเสาร์-อาทิตย์และวันหยุดราชการ ตามเงื่อนไขเดียวกับแอปผู้ป่วย
                  </div>
                </Spin>
              </Card>
            </Col>

            <Col xs={24} lg={15}>
              <Card
                title={date ? `ช่วงเวลาที่ว่าง · ${date.format('dddd D MMM YYYY')}` : 'ช่วงเวลาที่ว่าง'}
                style={{ marginBottom: 16 }}
              >
                <Spin spinning={scheduleLoading}>{renderSlots()}</Spin>
              </Card>

              <Card title="สรุปการนัดหมาย">
                <Descriptions bordered size="small" column={1}>
                  <Descriptions.Item label="ผู้ป่วย">{patient.name || patient.cId || patient.hnId}</Descriptions.Item>
                  <Descriptions.Item label={providerRoleLabel}>
                    {doctor ? doctor.fullname || doctor.email : <span style={{ color: '#bbb' }}>ยังไม่ได้เลือก</span>}
                  </Descriptions.Item>
                  <Descriptions.Item label="วันและเวลา">
                    {date && slot !== null ? (
                      `${date.format('D MMM YYYY')} เวลา ${minuteOfWeekToLabel(slot)} น.`
                    ) : (
                      <span style={{ color: '#bbb' }}>ยังไม่ได้เลือก</span>
                    )}
                  </Descriptions.Item>
                </Descriptions>
                {/* ค่าบริการชุดเดียวกับที่ผู้ป่วยเห็นตอนกดยืนยันในแอป เพื่อให้แจ้งผู้ป่วยได้ตรงกัน */}
                <Alert
                  style={{ marginTop: 12 }}
                  type="info"
                  message="อัตราค่าบริการ"
                  description="การใช้ระบบโทรเวชกรรม (Telemedicine) ครั้งละ 100 บาท (ไม่สามารถเบิกได้)"
                />
                <Button
                  type="primary"
                  size="large"
                  block
                  style={{ marginTop: 16 }}
                  disabled={!canSubmit}
                  onClick={() => setConfirmVisible(true)}
                >
                  ยืนยันการนัดหมาย
                </Button>
              </Card>
            </Col>
          </Row>
        </>
      )}

      <Modal
        title="ตรวจสอบข้อมูลก่อนยืนยัน"
        visible={confirmVisible}
        onOk={onConfirm}
        onCancel={() => setConfirmVisible(false)}
        confirmLoading={submitting}
        okText="ยืนยัน"
        cancelText="กลับไปแก้ไข"
      >
        {canSubmit ? (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="ผู้ป่วย">{patient.name || patient.cId}</Descriptions.Item>
            <Descriptions.Item label="HN">{formatHn(patient.hnId) || '-'}</Descriptions.Item>
            <Descriptions.Item label={providerRoleLabel}>{doctor.fullname || doctor.email}</Descriptions.Item>
            <Descriptions.Item label="วันที่">{date.format('dddd D MMM YYYY')}</Descriptions.Item>
            <Descriptions.Item label="เวลา">{minuteOfWeekToLabel(slot)} น.</Descriptions.Item>
          </Descriptions>
        ) : null}
        <Alert
          style={{ marginTop: 12 }}
          type="info"
          message="อัตราค่าบริการ"
          description="การใช้ระบบโทรเวชกรรม (Telemedicine) ครั้งละ 100 บาท (ไม่สามารถเบิกได้)"
        />
      </Modal>
    </div>
  )
}

const CallCenterBooking = (props) => <Layout {...props} render={({ userInfo }) => <Content userInfo={userInfo} />} />

export default CallCenterBooking
